import { zodResolver } from "@hookform/resolvers/zod"
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Divider } from "@mui/material"
import IconButton from "@mui/material/IconButton"
import { CloseOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import React, { useMemo } from "react"
import { Controller, useForm } from "react-hook-form"

import useHandleCloseWithDataSave from "../hooks/useHandleCloseWithDataSave"

import { AutoComplete } from "@/components/common/Autocomplete"
import { Button } from "@/components/common/Button"
import useGetSalesPerson from "@/hooks/api/useGetSalesPerson"
import { UpdateSalesmanSchema } from "@/screens/Companies/types/dialogProps"
import type { UpdateSalesmanDialogProps, UpdateSalesmanProps } from "@/screens/Companies/types/dialogProps"
import type { Option } from "@/types/form-fields"

const defaultValue: Option<string> = { value: "", label: "" }

export const EditSalesPersonDialog: FC<UpdateSalesmanDialogProps> = ({ activeEditLocation, onClose, onSubmit }) => {
  const { salesPersons, fetching } = useGetSalesPerson()

  const methods = useForm({
    values: {
      salesman: activeEditLocation?.salesman
        ? ({
            value: activeEditLocation?.salesman?.externalSystemId?.toString(),
            label: activeEditLocation?.salesman?.name || "",
          } as Option<string>)
        : defaultValue,
    },
    resolver: zodResolver(UpdateSalesmanSchema),
    mode: "onBlur",
  })

  const {
    formState: { isValid, isDirty },
  } = methods

  const canSubmit = isValid && isDirty && !fetching

  const salesPersonOptions: Option<string>[] = useMemo(
    () =>
      salesPersons.map(({ name, externalSystemId }) => ({
        label: name ?? "",
        value: externalSystemId.toString(),
      })),
    [salesPersons]
  )

  const handleSave = async (value: UpdateSalesmanProps) => {
    const isValid = await methods.trigger()
    if (isValid) {
      onSubmit(value)
      onClose({ data: { ...value }, reason: "success" })
    }
  }

  const handleClose = useHandleCloseWithDataSave(methods.getValues, onClose)

  return (
    <Dialog open onClose={handleClose} fullWidth>
      <DialogTitle className='pl-10'>
        <span className='font-normal text-primary'>Update Salesperson</span>
      </DialogTitle>
      <IconButton aria-label='close' onClick={handleClose} className='absolute right-4 top-4 text-primary'>
        <CloseOutlined />
      </IconButton>
      <Divider />
      <DialogContent classes={{ root: "p-10" }}>
        <form>
          <Controller
            name='salesman'
            control={methods.control}
            render={() => (
              <Box data-testid='edit-location-form-salesman'>
                <AutoComplete<Option<string>>
                  label='Salesperson'
                  placeholder='Select Salesperson'
                  name='salesman'
                  id='salesman'
                  options={salesPersonOptions}
                  getOptionLabel={({ label }) => label}
                  control={methods.control}
                  clearOnEscape
                  defaultEmptyState={defaultValue}
                />
              </Box>
            )}
          />
        </form>
      </DialogContent>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button variant='primary' appearance='outlined' onClick={handleClose}>
          Cancel
        </Button>
        <Button variant='primary' disabled={!canSubmit} onClick={methods.handleSubmit(handleSave)}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}
